import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { getKeys, saveKeys, linkedinBrowserStatus, installLinkedinBrowser, testEmail, connectRedirectUri, connectorStatus } from "../lib/api";
import KeyFields from "./KeyFields";
import TeamSettings from "./TeamSettings";
import FieldsSettings from "./FieldsSettings";
import ContactSettings from "./ContactSettings";
import SetupWizard from "./SetupWizard";

export default function SettingsModal({ close }) {
  const [saved, setSaved] = useState({});
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [liBrowser, setLiBrowser] = useState(null);
  const [liBusy, setLiBusy] = useState(false);
  const [emailTest, setEmailTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [redirectUri, setRedirectUri] = useState("");
  const [wizard, setWizard] = useState(false);
  const [connector, setConnector] = useState(null);
  const [checkingConnector, setCheckingConnector] = useState(false);

  useEffect(() => { getKeys().then(setSaved).catch(() => toast.error("Could not load settings")); linkedinBrowserStatus().then(r => setLiBrowser(r.installed)).catch(() => {}); connectRedirectUri().then(r => setRedirectUri(r.redirect_uri)).catch(() => {}); }, []);

  const runEmailTest = async () => {
    setTesting(true); setEmailTest(null);
    try {
      if (Object.keys(values).length) { setSaved(await saveKeys(values)); setValues({}); }
      setEmailTest(await testEmail());
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const checkConnector = async () => {
    setCheckingConnector(true); setConnector(null);
    try {
      if (Object.keys(values).length) { setSaved(await saveKeys(values)); setValues({}); }
      setConnector(await connectorStatus());
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    finally { setCheckingConnector(false); }
  };

  const installBrowser = async () => {
    setLiBusy(true);
    try { await installLinkedinBrowser(); setLiBrowser(true); toast.success("LinkedIn browser installed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    finally { setLiBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      // Only send fields the user touched; blank untouched fields must not wipe saved keys.
      const next = await saveKeys(values);
      setSaved(next); setValues({});
      toast.success("Settings saved");
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="modal login-card setup-card" onClick={(e) => e.stopPropagation()} data-testid="settings-modal">
        <button className="modal-close" onClick={close}><X size={16} /></button>
        <h2>Settings</h2>
        <p>Keys are stored only on this computer, and also written to the linked .env file when one is set under Database. Click <b>Get key</b> next to a field to open the page where that key is created. Social accounts (yours and the company's) are connected on <b>Social → Accounts</b>; here you only add each platform's developer app keys. Untouched fields keep what's saved. To remove a key, click into its field, type a character, then delete it.</p>
        {redirectUri && <p className="muted" data-testid="oauth-redirect-uri">Social accounts connect by logging in on <b>Social → Accounts</b>; nothing needs setting up here. Only if you use your own developer app (under Advanced): add <code>{redirectUri}</code> as its redirect URL.</p>}
        <button className="outline-button" onClick={() => setWizard(true)} data-testid="open-setup-guide">Open the setup guide</button>
        <TeamSettings />
        <ContactSettings />
        <FieldsSettings />
        <KeyFields values={values} saved={saved} onChange={(k, v) => setValues(s => ({ ...s, [k]: v }))} />
        <fieldset className="key-group">
          <legend>Check email connection</legend>
          <p>Saves your changes, then tries to log in to sending (SMTP) and reply detection (IMAP). Nothing is sent.</p>
          <button className="outline-button" onClick={runEmailTest} disabled={testing} data-testid="email-test">{testing ? "Testing…" : "Test email connection"}</button>
          {emailTest && ["smtp", "imap"].map(k => (
            <p key={k} data-testid={`email-test-${k}`}>{k === "smtp" ? "Sending" : "Reply detection"}: {emailTest[k].ok ? "✓ connected" : `✗ ${emailTest[k].error}`}</p>
          ))}
        </fieldset>
        <fieldset className="key-group" data-testid="connector-check">
          <legend>Check the Facebook and Instagram sign-in service</legend>
          <p>Saves the address above, then asks the service if it is running and set up. Nothing is posted.</p>
          <button className="outline-button" onClick={checkConnector} disabled={checkingConnector} data-testid="connector-check-button">{checkingConnector ? "Checking…" : "Check the sign-in service"}</button>
          {connector && <p data-testid="connector-result" style={{ color: connector.ok ? "#15803d" : "#b42318" }}>{connector.ok ? "✓" : "✗"} {connector.detail} <small className="muted">({connector.url})</small></p>}
        </fieldset>
        <fieldset className="key-group">
          <legend>LinkedIn connection automation</legend>
          <p>Needs a one-time ~150 MB browser download.</p>
          {liBrowser ? <p data-testid="li-browser-installed">Browser installed.</p>
            : <button className="outline-button" onClick={installBrowser} disabled={liBusy || liBrowser === null} data-testid="li-browser-install">{liBusy ? "Downloading… (a few minutes)" : "Download browser"}</button>}
        </fieldset>
        {wizard && <SetupWizard close={() => { setWizard(false); getKeys().then(setSaved).catch(() => {}); }} />}
        <button className="primary-button full" onClick={save} disabled={busy} data-testid="settings-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}
